import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billingService';
import { authService } from '../services/authService';
import Navbar from '../components/Navbar';
import GraphVisualization from '../components/GraphVisualization';

// Chain constants and colors (matte theme - matching Network Explorer)
const CHAINS = {
    ethereum: { id: 'ethereum', name: 'Ethereum', color: '#c9a227', bgColor: 'rgba(201, 162, 39, 0.12)' },
    plume: { id: 'plume', name: 'Plume', color: '#9d6bb8', bgColor: 'rgba(157, 107, 184, 0.12)' }
};

// Matte theme colors (matching Network Explorer)
const THEME = {
    bg: '#0a0a0f',
    cardBg: '#0c0c12',
    borderColor: '#1a1a24',
    textPrimary: '#e5e5e5',
    textSecondary: '#6c7280',
    accent: '#5a6f8e',
    danger: '#da4b4b',
    warning: '#c9a227',
    success: '#4a9d6b'
};

// Table categories for organization
const TABLE_CATEGORIES = {
    primitives: {
        name: 'Primitives',
        description: 'Foundational blockchain data types',
        icon: 'bi-layers',
        color: '#5a6f8e',
        tables: [
            'canonical_tx', 'tx_traces', 'tx_enriched_live', 'transfers',
            'eth_tokens', 'plume_tokens', 'eth_pools', 'plume_pools',
            'eth_pool_prices', 'plume_pool_prices', 'tx_trace_frames'
        ]
    },
    graph: {
        name: 'Graph',
        description: 'Graph nodes and edges for network visualization',
        icon: 'bi-share',
        color: '#9b59b6',
        tables: [
            'graph_nodes', 'graph_edges', 'graph_nodes_recent_v', 'graph_edges_recent_v'
        ]
    },
    composite: {
        name: 'Composite',
        description: 'Derived analytics from multiple sources',
        icon: 'bi-diagram-3',
        color: '#c9a227',
        tables: [
            'sybil_tx_history', 'sybil_contract_activity', 'sybil_clusters',
            'sybil_logs_history', 'risk_labels_current', 'risk_labels_events',
            'flow_events', 'flow_1m',
            'contract_transactions', 'plume_overlap_contract_history',
            'centroid_spoke_transfers', 'high_commit', 'plume_spenders',
            'plume_relay_capital_onboard', 'metamorpho_transactions',
            'infra_liveness_events', 'known_contracts',
            'sybil_wallet_labels_v', 'risk_labels_current_v'
        ]
    },
    utility: {
        name: 'Utility',
        description: 'System tables and data pipelines',
        icon: 'bi-gear',
        color: '#6c7280',
        tables: [
            'schema_migrations', 'sybil_history_backfill_state', 'capability_cursors',
            'mv_contract_sybil_exposure', 'mv_cluster_contract_map', 'mv_labels_latest',
            'mv_labels_parse', 'mv_tx_traces_ingest', 'mv_graph_nodes_chains',
            'mv_consume_flow', 'mv_canonical_tx_ingest', 'mv_edges_from_labels_erc20_deployer',
            'mv_graph_edges_deployer', 'mv_edges_from_flow', 'mv_graph_nodes_tokens',
            'mv_graph_nodes_wallets', 'mv_flow_1m', 'mv_edges_from_labels_erc20',
            'kafka_tx_traces_raw', 'kafka_tx_enriched_raw', 'kafka_labels_raw',
            'kafka_flow_events', 'vw_top_targeted_contracts', 'vw_capability_status',
            'vw_contract_exposure_30d', 'vw_contract_exposure_monthly', 'vw_infra_liveness',
            'bridge_to_plume'
        ]
    }
};

// Get category for a table
const getTableCategory = (tableName) => {
    const name = tableName.toLowerCase();
    for (const [category, config] of Object.entries(TABLE_CATEGORIES)) {
        if (config.tables.includes(name)) {
            return category;
        }
    }
    // Default categorization based on naming patterns
    if (name.startsWith('mv_') || name.startsWith('kafka_') || name.startsWith('vw_')) {
        return 'utility';
    }
    if (name.includes('graph_node') || name.includes('graph_edge')) {
        return 'graph';
    }
    if (name.includes('sybil') || name.includes('risk') || name.includes('flow')) {
        return 'composite';
    }
    return 'primitives';
};

// Table descriptions with data source information
const TABLE_DESCRIPTIONS = {
    // Core transaction tables
    canonical_tx: {
        title: 'Canonical Transactions',
        description: 'All blockchain transactions in a normalized format. Ingested via Kafka from the tx_enriched_archive topic, includes transaction details, logs, and token transfers.',
        source: 'Real-time Kafka ingestion from blockchain RPC nodes'
    },
    tx_traces: {
        title: 'Transaction Traces',
        description: 'Execution traces for transactions showing internal calls, value transfers, and gas usage. Essential for MEV detection and contract interaction analysis.',
        source: 'Real-time Kafka ingestion from debug_traceTransaction RPC calls'
    },
    tx_enriched_live: {
        title: 'Live Transaction View',
        description: 'View over canonical_tx for real-time transaction access.',
        source: 'View on canonical_tx table'
    },
    transfers: {
        title: 'Token Transfers',
        description: 'Native and ERC20 token transfers extracted from transactions.',
        source: 'Extracted from canonical_tx by Hydra pipeline'
    },

    // Sybil detection tables
    sybil_tx_history: {
        title: 'Sybil Transaction History',
        description: 'Complete transaction history for wallets flagged as sybil. Stores all on-chain activity for detected sybil addresses for audit and analysis.',
        source: 'Backfilled from canonical_tx for addresses in risk_labels_current with sybil_wallet label'
    },
    sybil_contract_activity: {
        title: 'Sybil Contract Activity',
        description: 'Aggregated contract interaction patterns for sybil wallets. Shows which contracts sybil addresses interact with and how frequently.',
        source: 'Aggregated from sybil_tx_history by contract address'
    },
    sybil_clusters: {
        title: 'Sybil Clusters',
        description: 'Groups of addresses detected as coordinated sybil actors. Uses graph-based community detection (Louvain algorithm) and Jaccard similarity on contract interactions.',
        source: 'Rust sybil_detector heuristic using SCC/WCC community detection'
    },
    sybil_logs_history: {
        title: 'Sybil Event Logs',
        description: 'Event logs emitted by transactions from sybil wallets. Useful for understanding sybil behavior patterns.',
        source: 'Backfilled from canonical_tx logs for sybil addresses'
    },
    sybil_history_backfill_state: {
        title: 'Sybil Backfill State',
        description: 'Internal tracking table for sybil history backfill progress.',
        source: 'System table for pipeline state management'
    },

    // Risk labels
    risk_labels_current: {
        title: 'Risk Labels (Current)',
        description: 'Latest risk labels for addresses, contracts, and transactions. Stores sybil flags, risk scores, and classification metadata.',
        source: 'Aggregated from risk_labels_events, deduplicated by latest version'
    },
    risk_labels_events: {
        title: 'Risk Label Events',
        description: 'Historical audit log of all risk label changes. Tracks when labels were added, modified, or confidence scores changed.',
        source: 'Heuristic pipeline outputs via Kafka labels topic'
    },

    // Graph tables
    graph_nodes: {
        title: 'Graph Nodes',
        description: 'Wallet, token, contract, and chain nodes for graph visualization. Stores node metadata and properties.',
        source: 'Materialized from risk_labels and canonical_tx via MVs'
    },
    graph_edges: {
        title: 'Graph Edges',
        description: 'Relationships between graph nodes: transfers, deployments, swaps, approvals. Enables graph traversal queries.',
        source: 'Materialized from flow_events and risk_labels via MVs'
    },

    // Ethereum tables
    eth_tokens: {
        title: 'Ethereum Tokens',
        description: 'ERC20 token contracts discovered on Ethereum mainnet. Includes symbol, name, decimals, and first seen timestamp.',
        source: 'Discovered by scanning canonical_tx for transfer/approve method calls'
    },
    eth_pools: {
        title: 'Ethereum DEX Pools',
        description: 'Uniswap V2/V3 and other DEX liquidity pools on Ethereum. Includes token pairs, fee tiers, and swap statistics.',
        source: 'Discovered by scanning swap events and factory contract logs'
    },
    eth_pool_prices: {
        title: 'Ethereum Pool Prices',
        description: 'Price data for DEX pools on Ethereum.',
        source: 'Calculated from pool reserve ratios and swap events'
    },
    contract_transactions: {
        title: 'Contract Transactions',
        description: 'Contract method calls extracted from transactions. Used for sybil detection based on similar contract interaction patterns.',
        source: 'Extracted from canonical_tx by Hydra pipeline'
    },
    known_contracts: {
        title: 'Known Contracts',
        description: 'Curated list of well-known contracts (DEX routers, stablecoins) for enrichment and labeling.',
        source: 'Manually curated seed data + automated discovery'
    },
    metamorpho_transactions: {
        title: 'MetaMorpho Transactions',
        description: 'Deposit and withdrawal transactions for the MetaMorphoV1_1 vault contract on Plume. Tracks individual user interactions.',
        source: 'Extracted from canonical_tx filtering by MetaMorpho contract address and deposit/withdraw method selectors'
    },

    // Plume tables
    plume_tokens: {
        title: 'Plume Tokens',
        description: 'ERC20/ERC-3643 token contracts discovered on Plume Network. Found by scanning for transfer, transferFrom, and approve calls.',
        source: 'Discovered by scanning Plume canonical_tx for token method selectors'
    },
    plume_pools: {
        title: 'Plume DEX Pools',
        description: 'Liquidity pools on Plume Network DEXes. Includes token pairs and pool metadata.',
        source: 'Discovered from Plume swap events and pool factory logs'
    },
    plume_pool_prices: {
        title: 'Plume Pool Prices',
        description: 'Price data for DEX pools on Plume Network.',
        source: 'Calculated from pool reserve ratios'
    },
    plume_spenders: {
        title: 'Plume Token Spenders',
        description: 'Addresses approved to spend tokens on Plume. Extracted from approve() calls to top tokens. Useful for identifying DeFi protocols.',
        source: 'Extracted from approve() calls in canonical_tx for top 100 Plume tokens'
    },
    plume_relay_capital_onboard: {
        title: 'Plume Relay Onboarding',
        description: 'Wallet onboarding via Relay permit2TransferAndMulticall. Tracks funds bridged through the Relay wallet to ApprovalProxy.',
        source: 'Extracted from Relay wallet transactions to ApprovalProxy using permit2 calldata decoding'
    },
    plume_overlap_contract_history: {
        title: 'Plume Overlap Contract History',
        description: 'Contract interaction history for wallets in the sybil overlap analysis. Stores method signatures and transaction details per contract.',
        source: 'Generated from canonical_tx for wallets in overlap.csv analysis set'
    },
    centroid_spoke_transfers: {
        title: 'Centroid-Spoke Transfers',
        description: 'Native PLUME transfer relationships between the CENTROID funding wallet and spoke addresses. Tracks net flow and interaction patterns.',
        source: 'Aggregated from canonical_tx native transfers involving the Centroid address'
    },
    high_commit: {
        title: 'High Commitment Wallets',
        description: 'MetaMorpho vault users with high time commitment (>10 days) and significant deposits (>1B raw units) who are also on the Plume leaderboard. Flags sybil cluster membership.',
        source: 'Filtered from MetaMorpho analysis, cross-referenced with Plume leaderboard and CENTROID sybil cluster'
    },

    // Flow and analytics
    flow_events: {
        title: 'Flow Events',
        description: 'Token flow events with USD values and risk scores. Landing table for real-time flow analysis.',
        source: 'Real-time Kafka ingestion from flow_events topic'
    },
    flow_1m: {
        title: 'Flow 1-Minute Rollup',
        description: 'One-minute aggregated flow statistics by chain and token. Used for dashboard metrics.',
        source: 'SummingMergeTree rollup from flow_events'
    },

    // System tables (no description needed, but included for completeness)
    schema_migrations: {
        title: 'Schema Migrations',
        description: 'Tracks applied database migrations.',
        source: 'System table for migration state'
    }
};

// Get table description with fallback
const getTableDescription = (tableName) => {
    const name = tableName.toLowerCase();
    if (TABLE_DESCRIPTIONS[name]) {
        return TABLE_DESCRIPTIONS[name];
    }
    // Check for pattern matches
    if (name.startsWith('mv_')) {
        return {
            title: 'Materialized View',
            description: 'Materialized view for data transformation and ingestion.',
            source: 'Automatically maintained by ClickHouse'
        };
    }
    if (name.startsWith('vw_') || name.endsWith('_v')) {
        return {
            title: 'View',
            description: 'SQL view for querying underlying tables.',
            source: 'Computed on-demand from source tables'
        };
    }
    if (name.startsWith('kafka_')) {
        return {
            title: 'Kafka Source',
            description: 'Kafka engine table for streaming data ingestion.',
            source: 'Real-time Kafka/Redpanda topic consumer'
        };
    }
    return null;
};

// Map table names to their supported chains
const getTableChains = (tableName) => {
    const name = tableName.toLowerCase();
    
    // Plume-only tables
    if (name.startsWith('plume_') || name.includes('_plume')) {
        return ['plume'];
    }
    
    // Ethereum-only tables
    if (name.startsWith('eth_') || name.includes('_eth') || name === 'metamorpho_transactions') {
        return ['ethereum'];
    }
    
    // Tables that have both chains
    if (['canonical_tx', 'tx_traces', 'tx_enriched_live', 'transfers'].includes(name)) {
        return ['ethereum', 'plume'];
    }
    
    // Graph tables (multi-chain)
    if (name.startsWith('graph_') || name.includes('_graph')) {
        return ['ethereum', 'plume'];
    }
    
    // Sybil tables (currently Ethereum-focused but could be multi-chain)
    if (name.startsWith('sybil_') || name.includes('_sybil')) {
        return ['ethereum'];
    }
    
    // Risk labels (multi-chain)
    if (name.startsWith('risk_labels')) {
        return ['ethereum', 'plume'];
    }
    
    // Internal/system tables - no chain indicator
    if (name.startsWith('kafka_') || name.startsWith('mv_') || name.startsWith('vw_') || 
        name.endsWith('_v') || name === 'schema_migrations' || name.includes('_backfill_')) {
        return [];
    }
    
    // Default to both chains for data tables
    return ['ethereum', 'plume'];
};

const Dashboard = ({ embedded = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [progressMessage, setProgressMessage] = useState('');
    const [tables, setTables] = useState([]);
    const [tablesLoading, setTablesLoading] = useState(true);
    const [expandedTable, setExpandedTable] = useState(null);
    const [chainFilters, setChainFilters] = useState({ ethereum: true, plume: true });
    const [selectedCategory, setSelectedCategory] = useState('all');
    
    // Live streaming state
    const [liveViewTable, setLiveViewTable] = useState(null);
    const [liveData, setLiveData] = useState(null);
    const [liveLoading, setLiveLoading] = useState(false);
    const [liveError, setLiveError] = useState(null);
    const [isLiveStreaming, setIsLiveStreaming] = useState(false);
    const liveIntervalRef = useRef(null);
    
    // Dashboard user assignment state
    const [dashboardUsers, setDashboardUsers] = useState([]);
    const [tableAssignments, setTableAssignments] = useState(() => {
        // Load from localStorage on init
        const saved = localStorage.getItem('tableAssignments');
        return saved ? JSON.parse(saved) : {};
    });
    
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const graphRef = useRef(null);

    const tier = user?.tier || 'free';
    const isAdmin = user?.is_admin || false;
    const canUseGraph = tier !== 'free' || isAdmin;

    // Graph API URL for ClickHouse tables
    const graphApiUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3001'
        : '/graph-api';

    // Fetch live data for a table
    const fetchLiveData = useCallback(async (tableName) => {
        if (!tableName) return;
        
        setLiveLoading(true);
        setLiveError(null);
        
        try {
            const chainParam = chainFilters.ethereum && !chainFilters.plume ? 'ethereum' :
                              !chainFilters.ethereum && chainFilters.plume ? 'plume' : '';
            const url = `${graphApiUrl}/v1/clickhouse/tables/${tableName}/live${chainParam ? `?chain=${chainParam}` : ''}`;
            
            const response = await fetch(url, { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setLiveData(data);
                } else {
                    setLiveError(data.error || 'Failed to fetch live data');
                }
            } else {
                setLiveError(`Failed to fetch: ${response.status}`);
            }
        } catch (err) {
            console.error('Error fetching live data:', err);
            setLiveError(err.message);
        } finally {
            setLiveLoading(false);
        }
    }, [graphApiUrl, chainFilters]);

    // Start/stop live streaming
    const toggleLiveStreaming = useCallback((tableName) => {
        if (liveIntervalRef.current) {
            clearInterval(liveIntervalRef.current);
            liveIntervalRef.current = null;
        }
        
        if (isLiveStreaming && liveViewTable === tableName) {
            // Stop streaming
            setIsLiveStreaming(false);
        } else {
            // Start streaming
            setIsLiveStreaming(true);
            setLiveViewTable(tableName);
            fetchLiveData(tableName);
            
            // Poll every 3 seconds
            liveIntervalRef.current = setInterval(() => {
                fetchLiveData(tableName);
            }, 3000);
        }
    }, [isLiveStreaming, liveViewTable, fetchLiveData]);

    // Open live view for a table
    const openLiveView = (tableName) => {
        setLiveViewTable(tableName);
        setExpandedTable(null);
        fetchLiveData(tableName);
    };

    // Close live view
    const closeLiveView = () => {
        if (liveIntervalRef.current) {
            clearInterval(liveIntervalRef.current);
            liveIntervalRef.current = null;
        }
        setLiveViewTable(null);
        setLiveData(null);
        setIsLiveStreaming(false);
    };

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (liveIntervalRef.current) {
                clearInterval(liveIntervalRef.current);
            }
        };
    }, []);

    // Re-fetch live data when chain filters change
    useEffect(() => {
        if (liveViewTable) {
            fetchLiveData(liveViewTable);
        }
    }, [chainFilters, liveViewTable, fetchLiveData]);

    // Fetch ClickHouse tables on mount
    useEffect(() => {
        const fetchTables = async () => {
            setTablesLoading(true);
            try {
                const response = await fetch(`${graphApiUrl}/v1/clickhouse/tables`, { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setTables(data.tables || []);
                    }
                }
            } catch (err) {
                console.error('Error fetching ClickHouse tables:', err);
            } finally {
                setTablesLoading(false);
            }
        };
        fetchTables();
    }, [graphApiUrl]);

    // Fetch dashboard users for table assignment (admin only)
    useEffect(() => {
        const fetchDashboardUsers = async () => {
            if (!isAdmin) return;
            try {
                const result = await authService.adminGetUsers();
                if (result.ok) {
                    setDashboardUsers(result.data?.users || []);
                }
            } catch (err) {
                console.error('Error fetching dashboard users:', err);
            }
        };
        fetchDashboardUsers();
    }, [isAdmin]);

    // Graph setup state
    const [graphSetupLoading, setGraphSetupLoading] = useState({});
    const [graphSetupStatus, setGraphSetupStatus] = useState({});

    // Save table assignments to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('tableAssignments', JSON.stringify(tableAssignments));
    }, [tableAssignments]);

    // Setup graph tables for a user's dashboard table
    const setupGraphForTable = async (tableName) => {
        setGraphSetupLoading(prev => ({ ...prev, [tableName]: true }));
        setGraphSetupStatus(prev => ({ ...prev, [tableName]: null }));
        
        try {
            const response = await fetch(`${graphApiUrl}/v1/dashboard/table/${tableName}/graph/setup`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            
            const data = await response.json();
            
            if (data.success) {
                setGraphSetupStatus(prev => ({
                    ...prev,
                    [tableName]: {
                        success: true,
                        nodesInserted: data.stats?.nodesInserted || 0,
                        edgesInserted: data.stats?.edgesInserted || 0,
                        neo4jSynced: data.stats?.neo4jSynced || false
                    }
                }));
                console.log(`Graph setup complete for ${tableName}:`, data);
            } else {
                setGraphSetupStatus(prev => ({
                    ...prev,
                    [tableName]: { success: false, error: data.error }
                }));
            }
        } catch (err) {
            console.error(`Error setting up graph for ${tableName}:`, err);
            setGraphSetupStatus(prev => ({
                ...prev,
                [tableName]: { success: false, error: err.message }
            }));
        } finally {
            setGraphSetupLoading(prev => ({ ...prev, [tableName]: false }));
        }
    };

    // Toggle table assignment for a user
    const toggleTableAssignment = async (tableName, userId) => {
        const userAssignments = tableAssignments[userId] || [];
        const isAssigned = userAssignments.includes(tableName);
        
        if (isAssigned) {
            // Unassigning - just remove from list
            setTableAssignments(prev => ({
                ...prev,
                [userId]: userAssignments.filter(t => t !== tableName)
            }));
        } else {
            // Assigning - add to list and trigger graph setup
            setTableAssignments(prev => ({
                ...prev,
                [userId]: [...userAssignments, tableName]
            }));
            
            // Trigger graph table creation
            await setupGraphForTable(tableName);
        }
    };

    // Check if a table is assigned to a user
    const isTableAssigned = (tableName, userId) => {
        return (tableAssignments[userId] || []).includes(tableName);
    };

    // Handle post-checkout success/cancel
    useEffect(() => {
        const success = searchParams.get('success');
        const canceled = searchParams.get('canceled');

        if (success === '1') {
            // Refresh user data to get updated tier
            refreshUser().then(() => {
                setSuccessMessage('✅ Your subscription is active! Welcome to your new plan.');
                // Clear the URL parameter
                searchParams.delete('success');
                setSearchParams(searchParams);
                // Auto-hide after 10 seconds
                setTimeout(() => setSuccessMessage(''), 10000);
            });
        } else if (canceled === '1') {
            setErrorMessage('Upgrade canceled or not completed. Your plan remains unchanged.');
            // Clear the URL parameter
            searchParams.delete('canceled');
            setSearchParams(searchParams);
            // Auto-hide after 8 seconds
            setTimeout(() => setErrorMessage(''), 8000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleManageBilling = async () => {
        try {
            const result = await billingService.getPortalUrl();
            if (result.ok && result.url) {
                window.location.href = result.url;
            } else {
                alert('Failed to open billing portal. Please try again or contact support.');
            }
        } catch (error) {
            console.error('Billing portal error:', error);
            alert('Failed to open billing portal. Please try again.');
        }
    };

    const handleQuery = async () => {
        if (!query.trim()) {
            setErrorMessage('⚠️ Please enter a question');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        setLoading(true);
        setShowResults(true);
        setResults(null);
        setGraphData(null);
        setErrorMessage('');
        setSuccessMessage('');

        // Progress messages
        const progressSteps = [
            '🔍 Connecting to AI agent...',
            '📊 Exploring database schema...',
            '🧠 Understanding your question...',
            '⚙️ Generating SQL query...',
            '✓ Validating query syntax...',
            '⚡ Executing query...',
            '🎨 Building graph visualization...',
            '🔄 Processing results...'
        ];

        let stepIndex = 0;
        const progressInterval = setInterval(() => {
            setProgressMessage(progressSteps[stepIndex % progressSteps.length]);
            stepIndex++;
        }, 1000);

        try {
            const startTime = Date.now();
            const response = await fetch('/console/agentic-query', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: query })
            });

            clearInterval(progressInterval);
            setProgressMessage('');

            const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);

            if (!response.ok) {
                // Handle rate limit error
                if (response.status === 429) {
                    const rateLimitMessage = `⚠️ Rate limit exceeded. Your ${tier.toUpperCase()} plan allows limited requests per minute. Please wait a moment or upgrade your plan for higher limits.`;
                    setErrorMessage(rateLimitMessage);
                    setResults(rateLimitMessage);
                } else {
                    const errorText = await response.text();
                    throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
                }
            } else {
                const data = await response.json();

                // Check if graph data exists
                if (data && data.graph && data.graph.nodes && data.graph.edges) {
                    const nodeCount = data.graph.nodes.length;
                    const edgeCount = data.graph.edges.length;

                    setGraphData(data.graph);

                    // Build results text
                    let metaText = '';
                    if (data.meta && data.meta.summary) {
                        metaText += `📊 ${data.meta.summary}\n\n`;
                    }
                    metaText += `✅ Found ${nodeCount} nodes and ${edgeCount} edges\n`;
                    metaText += `⏱️ Completed in ${elapsed}s\n`;

                    if (data.meta) {
                        if (data.meta.steps) {
                            metaText += `🤖 Agent completed in ${data.meta.steps} steps\n`;
                        }
                        if (data.meta.sql) {
                            metaText += `\n📝 SQL Query:\n${data.meta.sql}\n`;
                        }
                        if (data.meta.notes) {
                            metaText += `\n💡 ${data.meta.notes}`;
                        }
                    }

                    setResults(metaText);

                    // Scroll to graph after a brief delay
                    setTimeout(() => {
                        if (graphRef.current) {
                            graphRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 300);
                } else {
                    setResults('⚠️ No graph data available for this question.\n\nTry asking about:\n• Token deployments\n• Sybil wallets\n• Recent deployers\n• Chain-specific activity');
                }
            }
        } catch (error) {
            clearInterval(progressInterval);
            setProgressMessage('');
            console.error('Query error:', error);
            setErrorMessage(`❌ Error: ${error.message}`);
            setResults(`❌ Error: ${error.message}\n\nCheck browser console for details.`);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !loading) {
            handleQuery();
        }
    };

    return (
        <div className="bg-dark text-light" style={{ minHeight: embedded ? 'auto' : '100vh' }}>
            {!embedded && (
                <Navbar
                    showUpgrade={tier === 'free' && !isAdmin}
                    onManageBilling={(tier === 'basic' || tier === 'enterprise') && !isAdmin && tier !== 'admin' ? handleManageBilling : null}
                />
            )}

            <div className={`container-fluid ${embedded ? 'px-0' : 'px-4 my-4'}`}>
                {/* Success/Error Messages */}
                {successMessage && (
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                        {successMessage}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setSuccessMessage('')}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {errorMessage && !showResults && (
                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                        {errorMessage}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setErrorMessage('')}
                            aria-label="Close"
                        ></button>
                    </div>
                )}

                {/* Full-width Tables Viewer with integrated Query Box */}
                <div style={{ 
                    backgroundColor: THEME.cardBg, 
                    border: embedded ? 'none' : `1px solid ${THEME.borderColor}`,
                    borderRadius: embedded ? '0' : '8px',
                    marginTop: embedded ? '0' : '1rem'
                }}>
                    {/* Query Box - integrated at top */}
                    <div style={{ 
                        padding: '1.25rem 1.5rem',
                        borderBottom: `1px solid ${THEME.borderColor}`,
                        backgroundColor: THEME.bg
                    }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="input-group" style={{ flex: 1 }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ask about blockchain data..."
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    disabled={loading}
                                    style={{
                                        borderRadius: '6px 0 0 6px',
                                        border: `1px solid ${THEME.borderColor}`,
                                        background: THEME.cardBg,
                                        color: THEME.textPrimary,
                                        padding: '0.75rem 1rem',
                                        fontSize: '0.95rem',
                                        fontFamily: 'Satoshi, sans-serif'
                                    }}
                                />
                                <button
                                    className="btn"
                                    style={{ 
                                        borderRadius: '0 6px 6px 0', 
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: '#5a6f8e',
                                        border: '1px solid #5a6f8e',
                                        color: '#fff',
                                        fontWeight: 500,
                                        fontFamily: 'Satoshi, sans-serif'
                                    }}
                                    onClick={handleQuery}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Working...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-search me-1"></i> Ask
                                        </>
                                    )}
                                </button>
                            </div>
                            <div style={{ color: THEME.textSecondary, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                {tier === 'admin' ? (
                                    <><i className="bi bi-shield-fill-check me-1"></i>Admin</>
                                ) : tier === 'enterprise' ? (
                                    <><i className="bi bi-star-fill me-1"></i>Enterprise</>
                                ) : tier === 'basic' ? (
                                    <><i className="bi bi-check-circle me-1"></i>Basic</>
                                ) : (
                                    <><i className="bi bi-info-circle me-1"></i>Free</>
                                )}
                        </div>
                    </div>
                </div>

                    {/* Tables Section */}
                    <div className="pt-3 px-3">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h4 className="mb-1" style={{ fontWeight: 700, letterSpacing: '-0.02em', textTransform: 'lowercase', color: THEME.textPrimary }}>
                                        available tables
                                    </h4>
                                    <p className="mb-0" style={{ fontSize: '0.9rem', textTransform: 'lowercase', color: THEME.textSecondary }}>
                                        clickhouse database schema • {tables.filter(t => {
                                            const chains = getTableChains(t.name);
                                            const category = getTableCategory(t.name);
                                            const chainMatch = chains.length === 0 || chains.some(c => chainFilters[c]);
                                            const categoryMatch = selectedCategory === 'all' || category === selectedCategory;
                                            return chainMatch && categoryMatch;
                                        }).length} tables
                                    </p>
                                </div>
                                <div className="d-flex gap-3 align-items-center">
                                    {/* Category filters */}
                                <div className="d-flex gap-2 align-items-center">
                                        <button
                                            className="btn btn-sm"
                                            style={{
                                                backgroundColor: selectedCategory === 'all' ? 'rgba(90, 111, 142, 0.2)' : 'transparent',
                                                border: `1px solid ${selectedCategory === 'all' ? THEME.accent : '#333'}`,
                                                color: selectedCategory === 'all' ? THEME.accent : '#666',
                                                padding: '0.35rem 0.7rem',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem'
                                            }}
                                            onClick={() => setSelectedCategory('all')}
                                        >
                                            All
                                        </button>
                                        {Object.entries(TABLE_CATEGORIES).map(([key, cat]) => (
                                            <button
                                                key={key}
                                                className="btn btn-sm d-flex align-items-center gap-1"
                                                style={{
                                                    backgroundColor: selectedCategory === key ? `${cat.color}20` : 'transparent',
                                                    border: `1px solid ${selectedCategory === key ? cat.color : '#333'}`,
                                                    color: selectedCategory === key ? cat.color : '#666',
                                                    padding: '0.35rem 0.7rem',
                                                    borderRadius: '4px',
                                                    fontSize: '0.8rem'
                                                }}
                                                onClick={() => setSelectedCategory(key)}
                                                title={cat.description}
                                            >
                                                <i className={`bi ${cat.icon}`} style={{ fontSize: '0.75rem' }}></i>
                                                {cat.name}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <div style={{ width: '1px', height: '24px', backgroundColor: '#333' }}></div>
                                    
                                    {/* Chain filters */}
                                    <div className="d-flex gap-2 align-items-center">
                                    <button
                                        className="btn btn-sm d-flex align-items-center gap-2"
                                        style={{
                                            backgroundColor: chainFilters.ethereum ? CHAINS.ethereum.bgColor : 'transparent',
                                            border: `1px solid ${chainFilters.ethereum ? CHAINS.ethereum.color : '#333'}`,
                                            color: chainFilters.ethereum ? CHAINS.ethereum.color : '#666',
                                                padding: '0.35rem 0.7rem',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem'
                                        }}
                                        onClick={() => setChainFilters(prev => ({ ...prev, ethereum: !prev.ethereum }))}
                                    >
                                        <span style={{ 
                                                width: '6px', 
                                                height: '6px', 
                                            borderRadius: '50%', 
                                            backgroundColor: chainFilters.ethereum ? CHAINS.ethereum.color : '#444'
                                        }}></span>
                                            ETH
                                    </button>
                                    <button
                                        className="btn btn-sm d-flex align-items-center gap-2"
                                        style={{
                                            backgroundColor: chainFilters.plume ? CHAINS.plume.bgColor : 'transparent',
                                            border: `1px solid ${chainFilters.plume ? CHAINS.plume.color : '#333'}`,
                                            color: chainFilters.plume ? CHAINS.plume.color : '#666',
                                                padding: '0.35rem 0.7rem',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem'
                                        }}
                                        onClick={() => setChainFilters(prev => ({ ...prev, plume: !prev.plume }))}
                                        >
                                            <span style={{ 
                                                width: '6px', 
                                                height: '6px', 
                                                borderRadius: '50%', 
                                                backgroundColor: chainFilters.plume ? CHAINS.plume.color : '#444'
                                            }}></span>
                                            PLM
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Live View Panel */}
                            {liveViewTable && (
                                <div className="mb-4 rounded" style={{ backgroundColor: THEME.bg, border: `1px solid ${THEME.borderColor}` }}>
                                    <div className="d-flex justify-content-between align-items-center p-3" style={{ borderBottom: `1px solid ${THEME.borderColor}` }}>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="bi bi-broadcast" style={{ color: isLiveStreaming ? THEME.success : THEME.textSecondary }}></i>
                                                <h5 className="mb-0" style={{ color: THEME.textPrimary, fontWeight: 600 }}>
                                                    <code style={{ color: THEME.warning }}>{liveViewTable}</code>
                                                </h5>
                                            </div>
                                            <button
                                                className="btn btn-sm d-flex align-items-center gap-2"
                                                style={{
                                                    backgroundColor: isLiveStreaming ? 'rgba(74, 157, 107, 0.15)' : 'rgba(90, 111, 142, 0.15)',
                                                    border: `1px solid ${isLiveStreaming ? THEME.success : THEME.accent}`,
                                                    color: isLiveStreaming ? THEME.success : THEME.accent,
                                                    padding: '0.3rem 0.75rem',
                                                    borderRadius: '4px',
                                                    fontSize: '0.8rem'
                                                }}
                                                onClick={() => toggleLiveStreaming(liveViewTable)}
                                    >
                                        <span style={{ 
                                            width: '8px', 
                                            height: '8px', 
                                            borderRadius: '50%', 
                                                    backgroundColor: isLiveStreaming ? THEME.success : THEME.accent,
                                                    animation: isLiveStreaming ? 'pulse 1.5s infinite' : 'none'
                                        }}></span>
                                                {isLiveStreaming ? 'Live' : 'Start Live'}
                                    </button>
                                            {!isLiveStreaming && (
                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: `1px solid ${THEME.borderColor}`,
                                                        color: THEME.textSecondary,
                                                        padding: '0.3rem 0.75rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.8rem'
                                                    }}
                                                    onClick={() => fetchLiveData(liveViewTable)}
                                                    disabled={liveLoading}
                                                >
                                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                                    Refresh
                                                </button>
                                            )}
                                </div>
                                        <button
                                            className="btn btn-sm"
                                            style={{ backgroundColor: 'transparent', border: 'none', color: THEME.textSecondary }}
                                            onClick={closeLiveView}
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                            </div>
                                    
                                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                                        {liveLoading && !liveData ? (
                                            <div className="text-center py-4">
                                                <div className="spinner-border spinner-border-sm me-2" style={{ color: THEME.accent }}></div>
                                                <span style={{ color: THEME.textSecondary }}>Loading data...</span>
                                            </div>
                                        ) : liveError ? (
                                            <div className="p-3" style={{ color: THEME.danger }}>
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                {liveError}
                                            </div>
                                        ) : liveData && liveData.rows ? (
                                            <div style={{ overflowX: 'auto', minWidth: '100%' }}>
                                                <table className="table table-sm mb-0" style={{ fontSize: '0.8rem', backgroundColor: 'transparent', minWidth: 'max-content' }}>
                                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: THEME.bg }}>
                                                        <tr>
                                                            {liveData.columns.map((col, i) => (
                                                                <th key={i} className="py-2 px-3" style={{ 
                                                                    backgroundColor: THEME.bg, 
                                                                    color: THEME.textSecondary, 
                                                                    fontSize: '0.7rem', 
                                                                    textTransform: 'uppercase',
                                                                    borderBottom: `1px solid ${THEME.borderColor}`,
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {col}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                <tbody>
                                                    {liveData.rows.map((row, rowIdx) => (
                                                        <tr key={rowIdx} style={{ 
                                                            borderBottom: `1px solid ${THEME.borderColor}`,
                                                            transition: 'background-color 0.3s ease',
                                                            animation: rowIdx === 0 && isLiveStreaming ? 'fadeIn 0.5s ease' : 'none'
                                                        }}>
                                                            {liveData.columns.map((col, colIdx) => {
                                                                const value = row[col];
                                                                const isHex = typeof value === 'string' && value.startsWith('0x');
                                                                const isLongHex = isHex && value.length > 20;
                                                                const isArray = Array.isArray(value);
                                                                const isLongString = typeof value === 'string' && value.length > 50;
                                                                const needsCopy = isLongHex || isArray || isLongString;
                                                                const fullValue = isArray ? JSON.stringify(value) : String(value);
                                                                
                                                                const handleCopy = () => {
                                                                    navigator.clipboard.writeText(fullValue);
                                                                };
                                                                
                                                                return (
                                                                    <td key={colIdx} className="py-2 px-3" style={{ 
                                                                        backgroundColor: 'transparent',
                                                                        color: THEME.textPrimary,
                                                                        whiteSpace: 'nowrap',
                                                                        fontSize: '0.75rem'
                                                                    }}>
                                                                        {value !== null ? (
                                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                                {isLongHex ? (
                                                                                    <code style={{ color: THEME.warning, fontSize: '0.75rem' }} title={value}>
                                                                                        {value.slice(0, 10)}...{value.slice(-6)}
                                                                                    </code>
                                                                                ) : isHex ? (
                                                                                    <code style={{ color: THEME.warning, fontSize: '0.75rem' }}>
                                                                                        {value}
                                                                                    </code>
                                                                                ) : isArray ? (
                                                                                    <span style={{ color: THEME.textSecondary }} title={fullValue}>
                                                                                        [{value.length} items]
                                                                                    </span>
                                                                                ) : isLongString ? (
                                                                                    <span title={value}>{value.slice(0, 40)}...</span>
                                                                                ) : (
                                                                                    String(value)
                                                                                )}
                                                                                {needsCopy && (
                                                                                    <button
                                                                                        onClick={handleCopy}
                                                                                        style={{
                                                                                            background: 'transparent',
                                                                                            border: 'none',
                                                                                            cursor: 'pointer',
                                                                                            padding: '2px',
                                                                                            color: THEME.textSecondary,
                                                                                            fontSize: '0.7rem',
                                                                                            opacity: 0.6,
                                                                                            transition: 'opacity 0.2s'
                                                                                        }}
                                                                                        onMouseEnter={(e) => e.target.style.opacity = 1}
                                                                                        onMouseLeave={(e) => e.target.style.opacity = 0.6}
                                                                                        title="Copy full value"
                                                                                    >
                                                                                        <i className="bi bi-copy"></i>
                                                                                    </button>
                                                                                )}
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ color: THEME.textSecondary }}>null</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4" style={{ color: THEME.textSecondary }}>
                                                No data available
                                            </div>
                                        )}
                                    </div>
                                    
                                    {liveData && (
                                        <div className="p-2 d-flex justify-content-between align-items-center" style={{ 
                                            borderTop: `1px solid ${THEME.borderColor}`,
                                            backgroundColor: THEME.bg
                                        }}>
                                            <span style={{ color: THEME.textSecondary, fontSize: '0.8rem' }}>
                                                Showing {liveData.row_count} most recent rows
                                                {liveData.order_column && <span> • ordered by <code>{liveData.order_column}</code></span>}
                                            </span>
                                            {isLiveStreaming && (
                                                <span style={{ color: THEME.success, fontSize: '0.8rem' }}>
                                                    <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.5rem', animation: 'pulse 1.5s infinite' }}></i>
                                                    Auto-refreshing every 3s
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {tablesLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border spinner-border-sm me-2" style={{ color: THEME.accent }}></div>
                                    <span style={{ color: THEME.textSecondary }}>Loading tables...</span>
                                </div>
                            ) : tables.length === 0 ? (
                                <div className="text-center py-5 rounded" style={{ border: `1px dashed ${THEME.borderColor}`, color: THEME.textSecondary }}>
                                    No tables found in database
                                </div>
                            ) : (
                                <div className="table-responsive rounded" style={{ maxHeight: liveViewTable ? 'calc(100vh - 680px)' : 'calc(100vh - 380px)', minHeight: '300px', overflowY: 'auto', backgroundColor: THEME.cardBg, border: `1px solid ${THEME.borderColor}` }}>
                                    <table className="table mb-0" style={{ fontSize: '0.9rem', backgroundColor: THEME.cardBg, width: '100%', tableLayout: 'fixed' }}>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: THEME.cardBg, borderBottom: `1px solid ${THEME.borderColor}`, zIndex: 1 }}>
                                            <tr>
                                                <th className="py-3 px-4" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '25%' }}>Table Name</th>
                                                <th className="py-3 px-4" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '12%' }}>Category</th>
                                                <th className="py-3 px-4" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '10%' }}>Chains</th>
                                                <th className="py-3 px-4" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '18%' }}>Engine</th>
                                                <th className="py-3 px-4 text-end" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '12%' }}>Rows</th>
                                                <th className="py-3 px-4 text-end" style={{ backgroundColor: THEME.cardBg, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase', fontSize: '0.75rem', color: THEME.textSecondary, width: '10%' }}>Size</th>
                                                <th className="py-3 px-4" style={{ backgroundColor: THEME.cardBg, width: '13%' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody style={{ backgroundColor: THEME.cardBg }}>
                                            {tables
                                                .filter(table => {
                                                    const chains = getTableChains(table.name);
                                                    const category = getTableCategory(table.name);
                                                    // Category filter
                                                    if (selectedCategory !== 'all' && category !== selectedCategory) return false;
                                                    // Always show system/internal tables (no chain)
                                                    if (chains.length === 0) return true;
                                                    // Show if any of the table's chains are enabled
                                                    return chains.some(c => chainFilters[c]);
                                                })
                                                .map((table, idx) => {
                                                const tableChains = getTableChains(table.name);
                                                const category = getTableCategory(table.name);
                                                const categoryConfig = TABLE_CATEGORIES[category];
                                                return (
                                                <React.Fragment key={idx}>
                                                    <tr 
                                                        style={{ 
                                                            borderBottom: `1px solid ${THEME.borderColor}`, 
                                                            backgroundColor: expandedTable === table.name ? '#151520' : THEME.cardBg,
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.15s ease'
                                                        }}
                                                        onClick={() => setExpandedTable(expandedTable === table.name ? null : table.name)}
                                                    >
                                                        <td className="py-3 px-4" style={{ backgroundColor: 'inherit' }}>
                                                            <code style={{ fontSize: '0.9rem', color: THEME.textPrimary }}>{table.name}</code>
                                                        </td>
                                                        <td className="py-3 px-4" style={{ backgroundColor: 'inherit' }}>
                                                            <span 
                                                                className="d-inline-flex align-items-center gap-1"
                                                                style={{ 
                                                                    backgroundColor: `${categoryConfig.color}15`,
                                                                    border: `1px solid ${categoryConfig.color}40`,
                                                                    color: categoryConfig.color,
                                                                    fontSize: '0.7rem',
                                                                    padding: '0.2rem 0.5rem',
                                                                    borderRadius: '3px',
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.05em'
                                                                }}
                                                            >
                                                                <i className={`bi ${categoryConfig.icon}`} style={{ fontSize: '0.65rem' }}></i>
                                                                {categoryConfig.name}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 px-4" style={{ backgroundColor: 'inherit' }}>
                                                            <div className="d-flex gap-1">
                                                                {tableChains.length === 0 ? (
                                                                    <span style={{ fontSize: '0.8rem', color: THEME.textSecondary }}>—</span>
                                                                ) : (
                                                                    tableChains.map(chain => (
                                                                        <span 
                                                                            key={chain}
                                                                            className="d-inline-flex align-items-center gap-1"
                                                                            style={{ 
                                                                                backgroundColor: CHAINS[chain].bgColor,
                                                                                border: `1px solid ${CHAINS[chain].color}40`,
                                                                                color: CHAINS[chain].color,
                                                                                fontSize: '0.7rem',
                                                                                padding: '0.15rem 0.4rem',
                                                                                borderRadius: '3px'
                                                                            }}
                                                                        >
                                                                            <span style={{ 
                                                                                width: '5px', 
                                                                                height: '5px', 
                                                                                borderRadius: '50%', 
                                                                                backgroundColor: CHAINS[chain].color
                                                                            }}></span>
                                                                            {chain === 'ethereum' ? 'ETH' : 'PLM'}
                                                                        </span>
                                                                    ))
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="py-3 px-4" style={{ backgroundColor: 'inherit' }}>
                                                            <span style={{ 
                                                                border: `1px solid ${THEME.borderColor}`, 
                                                                color: THEME.textSecondary, 
                                                                fontSize: '0.75rem', 
                                                                padding: '0.25rem 0.5rem',
                                                                borderRadius: '3px',
                                                                backgroundColor: 'transparent'
                                                            }}>{table.engine}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-end" style={{ backgroundColor: 'inherit' }}>
                                                            <span style={{ fontSize: '0.9rem', fontWeight: 600, color: THEME.textPrimary }}>{(table.row_count || 0).toLocaleString()}</span>
                                                        </td>
                                                        <td className="py-3 px-4 text-end" style={{ backgroundColor: 'inherit' }}>
                                                            <span style={{ fontSize: '0.85rem', color: THEME.textSecondary }}>{table.readable_size}</span>
                                                        </td>
                                                        <td className="py-3 px-4" style={{ backgroundColor: 'inherit' }}>
                                                            <div className="d-flex gap-2 justify-content-end">
                                                                <button
                                                                    className="btn btn-sm"
                                                                    style={{
                                                                        backgroundColor: liveViewTable === table.name ? `${THEME.success}20` : 'transparent',
                                                                        border: `1px solid ${liveViewTable === table.name ? THEME.success : THEME.borderColor}`,
                                                                        color: liveViewTable === table.name ? THEME.success : THEME.textSecondary,
                                                                        padding: '0.2rem 0.5rem',
                                                                        borderRadius: '3px',
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (liveViewTable === table.name) {
                                                                            closeLiveView();
                                                                        } else {
                                                                            openLiveView(table.name);
                                                                        }
                                                                    }}
                                                                    title="View live data"
                                                                >
                                                                    <i className="bi bi-broadcast"></i>
                                                                </button>
                                                                <i className={`bi bi-chevron-${expandedTable === table.name ? 'up' : 'down'}`} style={{ fontSize: '0.9rem', color: THEME.textSecondary }}></i>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {expandedTable === table.name && (
                                                        <tr style={{ backgroundColor: THEME.bg }}>
                                                            <td colSpan="7" className="px-4 py-4" style={{ backgroundColor: THEME.bg }}>
                                                                <div className="rounded p-4" style={{ backgroundColor: THEME.cardBg, border: `1px solid ${THEME.borderColor}` }}>
                                                                    {/* Table Description */}
                                                                    {(() => {
                                                                        const desc = getTableDescription(table.name);
                                                                        if (desc) {
                                                                            return (
                                                                                <div className="mb-4 pb-3" style={{ borderBottom: `1px solid ${THEME.borderColor}` }}>
                                                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                                                        <i className="bi bi-info-circle" style={{ color: THEME.accent }}></i>
                                                                                        <h6 className="mb-0" style={{ fontSize: '0.95rem', fontWeight: 600, color: THEME.textPrimary }}>
                                                                                            {desc.title}
                                                                                        </h6>
                                                                                    </div>
                                                                                    <p className="mb-2" style={{ fontSize: '0.85rem', lineHeight: 1.5, color: THEME.textSecondary }}>
                                                                                        {desc.description}
                                                                                    </p>
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <span style={{ fontSize: '0.75rem', color: THEME.textSecondary }}>
                                                                                            <i className="bi bi-database me-1"></i>
                                                                                            <strong>Data source:</strong>
                                                                                        </span>
                                                                                        <span style={{ fontSize: '0.75rem', color: THEME.accent }}>
                                                                                            {desc.source}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return null;
                                                                    })()}
                                                                    
                                                                    {/* User Dashboard Assignment Section - Admin Only */}
                                                                    {isAdmin && dashboardUsers.length > 0 && (
                                                                        <div className="mb-4 pb-3" style={{ borderBottom: `1px solid ${THEME.borderColor}` }}>
                                                                            <div className="d-flex align-items-center gap-2 mb-3">
                                                                                <i className="bi bi-person-plus" style={{ color: THEME.accent }}></i>
                                                                                <h6 className="mb-0" style={{ fontSize: '0.85rem', fontWeight: 600, color: THEME.textPrimary }}>
                                                                                    Add to User Dashboards
                                                                                </h6>
                                                                            </div>
                                                                            <p style={{ fontSize: '0.75rem', color: THEME.textSecondary, marginBottom: '0.75rem' }}>
                                                                                Select users to add this table view to their dashboard
                                                                            </p>
                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                {dashboardUsers.filter(u => !u.is_admin).map(dashUser => (
                                                                                    <button
                                                                                        key={dashUser.id}
                                                                                        className="btn btn-sm d-flex align-items-center gap-2"
                                                                                        style={{
                                                                                            backgroundColor: isTableAssigned(table.name, dashUser.id) ? `${THEME.success}20` : 'transparent',
                                                                                            border: `1px solid ${isTableAssigned(table.name, dashUser.id) ? THEME.success : THEME.borderColor}`,
                                                                                            color: isTableAssigned(table.name, dashUser.id) ? THEME.success : THEME.textSecondary,
                                                                                            padding: '0.35rem 0.75rem',
                                                                                            borderRadius: '20px',
                                                                                            fontSize: '0.75rem',
                                                                                            transition: 'all 0.2s ease',
                                                                                            opacity: graphSetupLoading[table.name] ? 0.7 : 1
                                                                                        }}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            toggleTableAssignment(table.name, dashUser.id);
                                                                                        }}
                                                                                        disabled={graphSetupLoading[table.name]}
                                                                                    >
                                                                                        {graphSetupLoading[table.name] ? (
                                                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                                                        ) : (
                                                                                            <i className={`bi ${isTableAssigned(table.name, dashUser.id) ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                                                                                        )}
                                                                                        <span style={{ 
                                                                                            maxWidth: '150px', 
                                                                                            overflow: 'hidden', 
                                                                                            textOverflow: 'ellipsis', 
                                                                                            whiteSpace: 'nowrap' 
                                                                                        }}>
                                                                                            {dashUser.email}
                                                                                        </span>
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                            {/* Graph Setup Status */}
                                                                            {graphSetupStatus[table.name] && (
                                                                                <div className="mt-2" style={{ fontSize: '0.7rem' }}>
                                                                                    {graphSetupStatus[table.name].success ? (
                                                                                        <span style={{ color: THEME.success }}>
                                                                                            <i className="bi bi-check-circle me-1"></i>
                                                                                            Graph created: {graphSetupStatus[table.name].nodesInserted} nodes, {graphSetupStatus[table.name].edgesInserted} edges
                                                                                            {graphSetupStatus[table.name].neo4jSynced && ' • Neo4j synced'}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span style={{ color: THEME.warning }}>
                                                                                            <i className="bi bi-exclamation-triangle me-1"></i>
                                                                                            Graph setup: {graphSetupStatus[table.name].error}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            {dashboardUsers.filter(u => !u.is_admin).length === 0 && (
                                                                                <p style={{ fontSize: '0.75rem', color: THEME.textSecondary, fontStyle: 'italic' }}>
                                                                                    No customer users available
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    
                                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                                        <h6 className="text-uppercase fw-bold mb-0" style={{ letterSpacing: '0.1rem', fontSize: '0.75rem', color: THEME.textSecondary }}>
                                                                        Column Schema ({table.columns?.length || 0} columns)
                                                                    </h6>
                                                                        <button
                                                                            className="btn btn-sm d-flex align-items-center gap-2"
                                                                            style={{
                                                                                backgroundColor: 'transparent',
                                                                                border: `1px solid ${THEME.success}`,
                                                                                color: THEME.success,
                                                                                padding: '0.3rem 0.75rem',
                                                                                borderRadius: '4px',
                                                                                fontSize: '0.75rem'
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openLiveView(table.name);
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-broadcast"></i>
                                                                            View Live Data
                                                                        </button>
                                                                    </div>
                                                                    
                                                                    <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                                        <table className="table table-sm mb-0" style={{ fontSize: '0.85rem', backgroundColor: THEME.cardBg }}>
                                                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: THEME.cardBg }}>
                                                                                <tr>
                                                                                    <th className="py-2 px-3" style={{ backgroundColor: THEME.cardBg, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05rem', color: THEME.textSecondary }}>Column</th>
                                                                                    <th className="py-2 px-3" style={{ backgroundColor: THEME.cardBg, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05rem', color: THEME.textSecondary }}>Type</th>
                                                                                    <th className="py-2 px-3 text-center" style={{ backgroundColor: THEME.cardBg, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05rem', color: THEME.textSecondary }}>Key</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {(table.columns || []).map((col, colIdx) => (
                                                                                    <tr key={colIdx} style={{ borderBottom: `1px solid ${THEME.borderColor}` }}>
                                                                                        <td className="py-2 px-3" style={{ backgroundColor: THEME.cardBg }}>
                                                                                            <code style={{ fontSize: '0.85rem', color: THEME.textPrimary }}>{col.name}</code>
                                                                                        </td>
                                                                                        <td className="py-2 px-3" style={{ backgroundColor: THEME.cardBg }}>
                                                                                            <span style={{ fontSize: '0.85rem', color: THEME.accent }}>{col.type}</span>
                                                                                        </td>
                                                                                        <td className="py-2 px-3 text-center" style={{ backgroundColor: THEME.cardBg }}>
                                                                                            {col.is_primary_key && <span className="me-1" style={{ border: `1px solid ${THEME.danger}`, color: THEME.danger, fontSize: '0.65rem', padding: '0.15rem 0.35rem', borderRadius: '2px' }}>PK</span>}
                                                                                            {col.is_sorting_key && <span style={{ border: `1px solid ${THEME.accent}`, color: THEME.accent, fontSize: '0.65rem', padding: '0.15rem 0.35rem', borderRadius: '2px' }}>SK</span>}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="mt-3 d-flex align-items-center justify-content-between" style={{ fontSize: '0.8rem', color: THEME.textSecondary }}>
                                <div className="d-flex align-items-center gap-3">
                                    <span>Click table row to expand schema</span>
                                    <span>•</span>
                                    <span>
                                        <i className="bi bi-broadcast me-1"></i>
                                        to view live data
                                    </span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <span style={{ color: THEME.danger }}>PK</span> = Primary Key
                                    <span>•</span>
                                    <span style={{ color: THEME.accent }}>SK</span> = Sorting Key
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Section */}
                {showResults && (
                    <div className="row justify-content-center mt-4 fade-in">
                        <div className="col-12">
                            {/* Summary Card */}
                            <div className="card bg-secondary-subtle text-light mb-3">
                                <div className="card-body">
                                    <h6 className="card-title mb-2">
                                        <i className="bi bi-check-circle text-success"></i> Query Results
                                    </h6>

                                    {/* Progress indicator */}
                                    {loading && progressMessage && (
                                        <div className="mb-3">
                                            <div className="progress mb-2" style={{ height: '8px' }}>
                                                <div
                                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                                    role="progressbar"
                                                    style={{ width: '100%' }}
                                                ></div>
                                            </div>
                                            <small className="text-muted">{progressMessage}</small>
                                        </div>
                                    )}

                                    <pre className="bg-dark p-3 rounded small mb-0" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                        {results || 'Waiting for results...'}
                                    </pre>
                                </div>
                            </div>

                            {/* Graph Card */}
                            <div className="card bg-secondary-subtle text-light" ref={graphRef}>
                                <div className="card-body">
                                    <h6 className="card-title mb-3">
                                        <i className="bi bi-diagram-3"></i> Query Results Graph
                                    </h6>

                                    {!canUseGraph ? (
                                        <div className="alert alert-warning">
                                            <i className="bi bi-lock-fill me-2"></i>
                                            Graph visualization is available on Basic and Enterprise tiers.
                                            <a href="/select-plan" className="ms-2">Upgrade now</a>
                                        </div>
                                    ) : graphData ? (
                                        <GraphVisualization graphData={graphData} />
                                    ) : (
                                        <div className="alert alert-info">
                                            <i className="bi bi-info-circle me-2"></i>
                                            No graph data to display yet. Run a query to see results.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;

